import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { confirmarTareaSap, leerOfPdf } from '../../api/client'

const ICONO = { ok: '✓', nuevo: '＋', reemplazado: '⇄', diferente: '✗', sin_codigo: '✗', falta: '?', ilegible: '?' }
const COLOR = {
  ok: 'text-emerald-700 bg-emerald-50', nuevo: 'text-sky-700 bg-sky-50', reemplazado: 'text-sky-700 bg-sky-50',
  sin_codigo: 'text-rose-700 bg-rose-50',
  diferente: 'text-rose-700 bg-rose-50', falta: 'text-amber-800 bg-amber-50', ilegible: 'text-amber-800 bg-amber-50',
}

// Releasing the OT: the OF's PDF as SAP prints it. It is read and checked
// against the OT -- item, planned quantity, folio (free), released in SAP --
// before confirming. A difference can only go through with a reason. Used
// by the SAP queue and by the OT's panel (the board). onHecho(ot): the OT
// as it is after the release. archivoInicial: a PDF dropped on the task,
// read as soon as the form opens.
export default function ConfirmarOf({ tareaId, onHecho, onCancelar, archivoInicial = null }) {
  const { t } = useTranslation(['operations', 'common'])
  const [archivo, setArchivo] = useState(archivoInicial)
  const [rev, setRev] = useState(null)
  const [leyendo, setLeyendo] = useState(!!archivoInicial)
  const [folio, setFolio] = useState('')
  const [forzar, setForzar] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [nota, setNota] = useState('')
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState(false)

  const elegir = async (f) => {
    setArchivo(f); setRev(null); setError(''); setForzar(false)
    if (!f) return
    setLeyendo(true)
    try { const r = await leerOfPdf(tareaId, f); setRev(r); setFolio(r.datos.folio || '') }
    catch (err) { setError(err.response?.data?.detail?.message || t('ot.cola.of.lecturaFallo')) }
    setLeyendo(false)
  }
  useEffect(() => {
    if (!archivoInicial) return
    let vivo = true
    leerOfPdf(tareaId, archivoInicial)
      .then(r => { if (vivo) { setRev(r); setFolio(r.datos.folio || '') } })
      .catch(err => { if (vivo) setError(err.response?.data?.detail?.message || t('ot.cola.of.lecturaFallo')) })
      .finally(() => { if (vivo) setLeyendo(false) })
    return () => { vivo = false }
  }, [archivoInicial, tareaId, t])

  const legible = rev?.datos?.legible
  const puedeConfirmar = rev && !rev.folio_en_uso && (rev.coincide || (forzar && motivo.trim() && (legible || folio.trim())))
  const confirmar = async () => {
    setOcupado(true); setError('')
    try {
      const ot = await confirmarTareaSap(tareaId, {
        archivo, nota: nota.trim(), folio: legible ? undefined : folio.trim(),
        ...(rev.coincide ? {} : { forzar: true, motivo_diferencia: motivo.trim() }),
      })
      onHecho(ot)
    } catch (err) {
      const d = err.response?.data?.detail
      setError(d?.code === 'validacion'
        ? t('ot.faltanDatos', { lista: d.faltan.map(f => t(`ot.faltan.${f}`, { defaultValue: f })).join(', ') })
        : d?.message || t('ot.panel.fallo'))
    }
    setOcupado(false)
  }
  const valor = (campo, v) => (v == null || v === '' ? '—' : campo === 'cantidad' ? String(v).replace('.', ',') : String(v))

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 space-y-3">
      <p className="text-xs text-gray-600">{t('ot.cola.of.hint')}</p>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('ot.cola.pdfOf')} *</label>
        {archivo && <p className="text-xs text-gray-700 mb-1">📄 {archivo.name}</p>}
        <input type="file" accept="application/pdf,.pdf" className="block w-full text-xs" onChange={e => elegir(e.target.files?.[0] || null)} />
        {leyendo && <p className="text-xs text-gray-500 mt-1">{t('ot.cola.of.leyendo')}</p>}
      </div>

      {rev && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-800">{t('ot.cola.of.validacion')}</p>
          {!legible ? (
            <p className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">{t('ot.cola.of.ilegible')}</p>
          ) : (
            <table className="w-full text-xs border border-gray-200 rounded-md overflow-hidden">
              <thead className="bg-gray-50 text-gray-500">
                <tr><th className="text-left px-2 py-1.5 font-medium"></th><th className="text-left px-2 py-1.5 font-medium">{t('ot.cola.of.enOt')}</th>
                  <th className="text-left px-2 py-1.5 font-medium">{t('ot.cola.of.enOf')}</th><th className="px-2 py-1.5"></th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rev.checks.map(c => (
                  <tr key={c.campo}>
                    <td className="px-2 py-1.5 text-gray-600">{t(`ot.cola.of.campos.${c.campo}`)}</td>
                    <td className="px-2 py-1.5 font-mono text-gray-900">{c.campo === 'folio' || c.campo === 'estado_sap' ? '' : valor(c.campo, c.ot)}</td>
                    <td className="px-2 py-1.5 font-mono text-gray-900">{valor(c.campo, c.of)}{c.campo === 'cantidad' && rev.datos.unidad ? ` ${rev.datos.unidad}` : ''}</td>
                    <td className="px-2 py-1.5 text-right">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${COLOR[c.estado]}`}>
                        {ICONO[c.estado]} {t(`ot.cola.of.estados.${c.estado}`)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {legible && (
            <p className="text-[11px] text-gray-500">
              {[rev.datos.articulo_descripcion, rev.datos.almacen && t('ot.cola.of.almacen', { a: rev.datos.almacen }),
                rev.datos.fecha_vencimiento && t('ot.cola.of.vence', { f: rev.datos.fecha_vencimiento }),
                t('ot.cola.of.componentes', { count: rev.datos.n_componentes }),
                rev.datos.comentarios && t('ot.cola.of.comentarios', { c: rev.datos.comentarios })].filter(Boolean).join(' · ')}
            </p>
          )}
          {rev.folio_en_uso && (
            <p className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800">
              {t('ot.cola.of.folioEnUso', { folio: rev.datos.folio, ot: rev.folio_en_uso })}
            </p>
          )}
          {rev.folio_de_cancelada && (
            <p className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
              {t('ot.cola.of.deCancelada', { folio: rev.datos.folio, ot: rev.folio_de_cancelada })}
            </p>
          )}
          {!legible && (
            <div className="sm:w-1/3">
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('ot.cola.folioOf')} *</label>
              <input className="input font-mono" value={folio} onChange={e => setFolio(e.target.value)} />
            </div>
          )}
          {rev.coincide ? (
            <p className="text-xs text-emerald-700 font-medium">✓ {t('ot.cola.of.coincide', { folio: rev.datos.folio })}</p>
          ) : !rev.folio_en_uso && (
            <div className="rounded-md border border-rose-200 bg-rose-50/60 px-3 py-2 space-y-2">
              <label className="flex items-start gap-2 text-xs text-rose-900 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={forzar} onChange={e => setForzar(e.target.checked)} />
                <span>{legible ? t('ot.cola.of.forzar') : t('ot.cola.of.forzarIlegible')}</span>
              </label>
              {forzar && (
                <textarea rows={2} className="input" value={motivo} onChange={e => setMotivo(e.target.value)}
                  placeholder={t('ot.cola.of.motivoPlaceholder')} />
              )}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('ot.cola.nota')}</label>
            <input className="input" value={nota} onChange={e => setNota(e.target.value)} />
          </div>
        </div>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancelar} className="text-xs font-medium text-gray-600 px-3 py-1.5 rounded-md hover:bg-gray-100">
          {t('common:actions.cancel')}
        </button>
        <button type="button" disabled={ocupado || !puedeConfirmar} onClick={confirmar}
          className="text-xs font-medium bg-emerald-600 text-white rounded-md px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-40">
          {t('ot.cola.confirmarYLiberar')}
        </button>
      </div>
    </div>
  )
}
